import React, { useState, useEffect } from 'react';
import { Card, StatCard, Badge, PageHeader, Button } from '../../components/ui';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import api from '../../services/api';
import toast from 'react-hot-toast';

const PLAN_NAMES  = { pack_one:'Pack One', pack_two:'Pack Two', pack_advanced:'Pack Advanced' };
const STATUS_COLOR = { completed:'success', pending:'warning', failed:'danger', refunded:'info' };

export default function Revenue() {
  const [payments, setPayments] = useState([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(true);

  useEffect(() => { fetchRevenue(); }, []);

  const fetchRevenue = async () => {
    try {
      const r = await api.get('/superadmin/revenue');
      setPayments(r.data.payments || []);
      setTotal(r.data.total || 0);
    } catch {} finally { setLoading(false); }
  };

  const exportCSV = async () => {
    try {
      const r = await api.get('/superadmin/revenue/export', { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'revenue-export.csv'; a.click();
    } catch { toast.error('Export failed'); }
  };

  const monthlyData = () => {
    const map = {};
    payments.filter(p => p.status === 'completed').forEach(p => {
      const key = new Date(p.createdAt).toLocaleDateString('en', { month:'short', year:'2-digit' });
      map[key] = (map[key] || 0) + (p.amount || 0);
    });
    return Object.entries(map).slice(-6).map(([month, revenue]) => ({ month, revenue }));
  };

  const thisMonth = payments.filter(p => {
    const d = new Date(p.createdAt);
    const now = new Date();
    return p.status === 'completed' && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }).reduce((s, p) => s + (p.amount || 0), 0);

  const planBreakdown = {};
  payments.filter(p => p.status === 'completed').forEach(p => {
    planBreakdown[p.planId] = (planBreakdown[p.planId] || 0) + (p.amount || 0);
  });

  return (
    <div>
      <PageHeader title="Revenue" subtitle="Subscription revenue and payment history" action={
        <div style={{ display:'flex', gap:8 }}>
          <Button variant="secondary" size="sm" onClick={exportCSV}>⬇ Export CSV</Button>
          <Button variant="secondary" size="sm" onClick={fetchRevenue}>🔄 Refresh</Button>
        </div>
      } />

      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14, marginBottom:24 }}>
        <StatCard icon="💰" label="Total Revenue"  value={`€${total.toLocaleString()}`}          color="#F39C12" />
        <StatCard icon="📅" label="This Month"     value={`€${thisMonth.toLocaleString()}`}       color="#2ECC71" />
        <StatCard icon="✅" label="Completed"      value={payments.filter(p=>p.status==='completed').length} />
        <StatCard icon="⏳" label="Pending"        value={payments.filter(p=>p.status==='pending').length}   color="#F39C12" />
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:20, marginBottom:24 }}>
        <Card>
          <h3 style={{ fontWeight:700, marginBottom:20 }}>📊 Monthly Revenue (€)</h3>
          {monthlyData().length === 0 ? (
            <div style={{ textAlign:'center', padding:40, color:'#555' }}>No completed payments yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={monthlyData()}>
                <XAxis dataKey="month" tick={{ fill:'#555', fontSize:11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill:'#555', fontSize:11 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background:'#111', border:'1px solid rgba(46,204,113,0.2)', borderRadius:8, color:'#fff', fontSize:12 }} formatter={(v) => [`€${v}`, 'Revenue']} />
                <Bar dataKey="revenue" fill="#2ECC71" radius={[6,6,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card>
          <h3 style={{ fontWeight:700, marginBottom:20 }}>📦 Revenue by Plan</h3>
          {Object.keys(planBreakdown).length === 0 ? (
            <div style={{ textAlign:'center', padding:24, color:'#555' }}>No data</div>
          ) : Object.entries(planBreakdown).map(([planId, amount]) => (
            <div key={planId} style={{ marginBottom:16 }}>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
                <span style={{ fontSize:13, color:'#ccc' }}>{PLAN_NAMES[planId] || planId}</span>
                <span style={{ fontWeight:700, color:'#2ECC71', fontSize:14 }}>€{amount.toLocaleString()}</span>
              </div>
              <div style={{ background:'#1a1a1a', borderRadius:4, height:6 }}>
                <div style={{ width:`${Math.min(100,(amount/total)*100)}%`, height:'100%', borderRadius:4, background:'#2ECC71' }} />
              </div>
            </div>
          ))}
        </Card>
      </div>

      <Card style={{ padding:0, overflow:'hidden' }}>
        <div style={{ padding:'16px 20px', borderBottom:'1px solid rgba(46,204,113,0.08)', fontWeight:700 }}>
          Payment History
        </div>
        {loading ? <div style={{ padding:32, textAlign:'center', color:'#555' }}>Loading...</div> : (
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead>
                <tr style={{ background:'rgba(46,204,113,0.04)', borderBottom:'1px solid rgba(46,204,113,0.08)' }}>
                  {['Payment ID','Client','Plan','Amount','Gateway ID','Status','Date'].map(h => (
                    <th key={h} style={{ padding:'11px 16px', textAlign:'left', color:'#555', fontWeight:700, fontSize:11, textTransform:'uppercase', whiteSpace:'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {payments.length === 0 ? (
                  <tr><td colSpan={7} style={{ textAlign:'center', padding:40, color:'#555' }}>No payments yet</td></tr>
                ) : payments.map(p => (
                  <tr key={p.id} style={{ borderBottom:'1px solid rgba(255,255,255,0.03)' }}>
                    <td style={{ padding:'11px 16px', color:'#555', fontSize:11 }}>{p.id?.slice(0,8).toUpperCase()}</td>
                    <td style={{ padding:'11px 16px', color:'#ccc' }}>{p.userId?.slice(0,8)}...</td>
                    <td style={{ padding:'11px 16px', color:'#2ECC71', fontWeight:600, fontSize:12 }}>{PLAN_NAMES[p.planId]||p.planId}</td>
                    <td style={{ padding:'11px 16px', fontWeight:800, color:'#fff' }}>€{p.amount}</td>
                    <td style={{ padding:'11px 16px', color:'#555', fontSize:11 }}>{p.gatewayPaymentId||'—'}</td>
                    <td style={{ padding:'11px 16px' }}><Badge color={STATUS_COLOR[p.status]||'gray'}>{p.status}</Badge></td>
                    <td style={{ padding:'11px 16px', color:'#555', fontSize:11 }}>{p.createdAt?new Date(p.createdAt).toLocaleDateString():'—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
